import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { CannyService, CHAT_SUPPORT_TIERS } from '@galaxy/partner';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { FilterParams, HelpCenterService as HelpCenterAPIService, Origins } from '@vendasta/help-center';
import { Observable, Subscription, take } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';
import { AppConfigService } from '../app-config.service';
import { AssignedSalespersonService } from '../assigned-salesperson/assigned-salesperson.service';
import { HelpCase, HelpCaseId, HELP_PAGES, HELP_CASES } from './help-case-card/help-center-cases';
import { Article } from './help-search-card/help-center-articles';
import { HelpSideDrawerTemplateRefService } from './help-side-drawer/help-side-drawer-template-ref.service';
import { SupportNumberService } from '../common/help-menu';
import { PartnerService } from '../core/partner.service';

@Component({
  selector: 'app-help-center',
  templateUrl: './help-center.component.html',
  styleUrls: ['./help-center.component.scss'],
})
export class HelpCenterComponent implements OnInit, OnDestroy {
  @Input() partnerId: string;
  @Input() subscriptionTier: string;
  @Input() premium_support: boolean;
  @Input() standard_priority_support: boolean;

  title: string = this.translate.instant('HELP_CENTER.TITLE');
  helpCases: HelpCase[] = HELP_PAGES[HelpCaseId.MAIN].cases;
  helpHeader: string = HELP_PAGES[HelpCaseId.MAIN].header;
  helpCaseHistory: string[] = [HelpCaseId.MAIN];
  currentPageId: string = HelpCaseId.MAIN;
  bookMeetingUrl$: Observable<string>;
  subscriptions: Subscription[] = [];
  searchBar: UntypedFormControl = new UntypedFormControl();
  articles: Article[] = [];
  showLoadMore: boolean;
  cursor: string;

  isSearching = false;

  constructor(
    private readonly assignedSalespersonService: AssignedSalespersonService,
    private readonly helpCenterAPIService: HelpCenterAPIService,
    private helpSideDrawerTemplateRefService: HelpSideDrawerTemplateRefService,
    private translate: TranslateService,
    private cannyService: CannyService,
    private appConfigService: AppConfigService,
    private snackBarService: SnackbarService,
    private supportNumberService: SupportNumberService,
    private readonly partnerService: PartnerService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.supportNumberService
        .getSupportNumber()
        .pipe(take(1))
        .subscribe((number) => {
          if (number) {
            if (this.helpCases.find((hc) => hc.id === HelpCaseId.CALL_US) === undefined) {
              this.helpCases.unshift(HELP_CASES[HelpCaseId.CALL_US]);
            }
          }
        }),
    );

    // if the partner subscription tier is part of the standard support tiers, then add live chat to the help cases
    this.subscriptions.push(
      this.partnerService.partnerConfig$.pipe(take(1)).subscribe((partnerConfig) => {
        if (CHAT_SUPPORT_TIERS.includes(partnerConfig.subscription_tier)) {
          if (this.helpCases.find((hc) => hc.id === HelpCaseId.LIVE_CHAT) === undefined) {
            this.helpCases.push(HELP_CASES[HelpCaseId.LIVE_CHAT]);
          }
        }
      }),
    );
    this.bookMeetingUrl$ = this.assignedSalespersonService.bookMeetingUrl$;

    this.subscriptions.push(
      this.bookMeetingUrl$.pipe(take(1)).subscribe((number) => {
        if (number) {
          if (this.helpCases.find((hc) => hc.id === HelpCaseId.BOOK_MEETING) === undefined) {
            this.helpCases.push(HELP_CASES[HelpCaseId.BOOK_MEETING]);
          }
        }
      }),
    );

    this.isSearching = false;
    this.cursor = '';
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  displaySearch(): boolean {
    return this.currentPageId === HelpCaseId.MAIN || this.currentPageId === HelpCaseId.SEARCH;
  }

  search(onKeyUp = true): void {
    if (this.searchBar.value) {
      this.isSearching = true;
      if (onKeyUp) {
        this.articles = [];
      }

      const searchFilters: FilterParams = new FilterParams({
        origins: [
          Origins.ORIGINS_RESOURCE_CENTER_ZENDESK,
          Origins.ORIGINS_COMMUNITY_POST_ZENDESK,
          Origins.ORIGINS_PODCASTS_WP,
          Origins.ORIGINS_VIDEO_WP,
          Origins.ORIGINS_OPEN_API,
        ],
      });
      this.helpCenterAPIService
        .search(this.searchBar.value, 'en-us', searchFilters, this.cursor, 30)
        .pipe(
          map((resp) => {
            if (resp) {
              resp.results.forEach((r) => {
                // This keeps the types generic while we have no duplicate types from different sources
                r.origin = r.origin.replace(/(Conquer Local )|(Zendesk )/, '');
                this.articles.push(<Article>{ title: r.title, body: r.body, source: r.origin, link: r.link });
              });
              this.cursor = resp.nextCursor;
              this.showLoadMore = resp.hasMore;
            }
            if (this.currentPageId !== HelpCaseId.SEARCH) {
              this.currentPageId = HelpCaseId.SEARCH;
              this.helpCaseHistory.push(HelpCaseId.SEARCH);
            }
            return this.articles;
          }),
          finalize(() => {
            this.isSearching = false;
          }),
        )
        .subscribe();
    }
  }

  loadMoreClicked(): void {
    this.search(false);
  }

  handleHelpCaseClick(caseId: string): void {
    if (caseId === HelpCaseId.LIVE_CHAT) {
      this.helpSideDrawerTemplateRefService.showChat();
      return;
    }
    if (caseId === HelpCaseId.BOOK_MEETING) {
      this.subscriptions.push(
        this.bookMeetingUrl$.pipe(map((url) => window.open(url, '_blank', 'noopener'))).subscribe(),
      );
    }
    if (caseId === HelpCaseId.CANNY) {
      this.openCanny();
      return;
    }
    if (HELP_PAGES[caseId]) {
      if (HELP_PAGES[caseId].url) {
        window.open(HELP_PAGES[caseId].url, '_blank', 'noopener');
      } else {
        this.helpHeader = HELP_PAGES[caseId].header;
        this.helpCases = HELP_PAGES[caseId].cases;
        if (caseId !== HelpCaseId.MAIN && caseId !== this.currentPageId) {
          this.currentPageId = caseId;
          this.helpCaseHistory.push(caseId);
        }
      }
    }
  }

  back(): void {
    this.helpCaseHistory.pop();
    this.currentPageId = this.helpCaseHistory.slice(-1)[0];
    this.handleHelpCaseClick(this.currentPageId);
  }

  openCanny(): void {
    this.appConfigService.config$
      .pipe(
        take(1),
        switchMap((appConfig) => {
          return this.cannyService.getSingleSignOn(appConfig.unifiedUserId);
        }),
      )
      .subscribe({
        next: (ssoConfig) => {
          window.open(
            this.buildCannyRedirectUrl(ssoConfig.token, ssoConfig.companyId, `https://${ssoConfig.roadmapDomain}`),
            '_blank',
            'noopener',
          );
        },
        error: () => {
          this.snackBarService.openErrorSnack(this.translate.instant('HELP_CENTER.ERROR'));
        },
      });
  }

  buildCannyRedirectUrl(ssoToken: string, companyId: string, redirect: string): string {
    return `https://canny.io/api/redirects/sso?companyID=${companyId}&ssoToken=${ssoToken}&redirect=${redirect}`;
  }
}
